import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Headset, Package, Wrench, Truck, Award, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

// Import images
import tftLogo from '@/assets/tft-logo.png';
import heroProcess from '@/assets/hero-process.jpg';
import timelineHistory from '@/assets/timeline-history.png';
import heroLab from '@/assets/hero-lab.jpg';
import heroLiquid from '@/assets/hero-liquid-packaging.jpg';
import heroSolid from '@/assets/hero-solid-packaging.jpg';
import heroSecondary from '@/assets/hero-secondary-packaging.jpg';
import heroPalletizing from '@/assets/hero-palletizing-realistic.jpg';
import heroServices from '@/assets/hero-services.jpg';
import heroTurnkey from '@/assets/hero-turnkey.jpg';
import milkCartons from '@/assets/dairy-products/milk-cartons.jpg';
import companyHero from '@/assets/company-hero.jpg';

// Industries
import dairyImg from '@/assets/dairy-products/milk-cartons.jpg';
import beverageImg from '@/assets/industries/beverage.jpg';
import bakeryImg from '@/assets/industries/bakery.jpg';
import meatFishImg from '@/assets/industries/meat-fish.jpg';
import oilsImg from '@/assets/industries/oils.jpg';
import spreadsImg from '@/assets/industries/spreads.jpg';
import fruitsVegImg from '@/assets/industries/fruits-vegetables.jpg';
import readyMealsImg from '@/assets/industries/ready-meals.jpg';

// Partners
import spxflow from '@/assets/partners/spxflow-alt.png';
import seital from '@/assets/partners/seital.png';
import plevnik from '@/assets/partners/plevnik.png';
import tecnal from '@/assets/partners/tecnal.png';
import gramEquipment from '@/assets/partners/gram-equipment.png';
import galdi from '@/assets/partners/galdi-correct.png';
import elopak from '@/assets/partners/elopak-red.jpg';
import trepko from '@/assets/partners/trepko.png';
import buhmann from '@/assets/partners/buhmann.png';
import gampack from '@/assets/partners/gampack.png';
import roboteh from '@/assets/partners/roboteh.png';
import cartobol from '@/assets/partners/cartobol.jpg';
import pattyn from '@/assets/partners/pattyn.png';
import serac from '@/assets/partners/serac.png';
import foss from '@/assets/partners/foss.png';
import xnext from '@/assets/partners/xnext.png';
import comac from '@/assets/partners/comac.jpg';
import cftGroup from '@/assets/partners/cft-group.png';
import mecatherm from '@/assets/partners/mecatherm.png';
import vmi from '@/assets/partners/vmi.png';
import shickEsteve from '@/assets/partners/shick-esteve.png';
import bfrSystems from '@/assets/partners/bfr-systems.png';
import enoopFood from '@/assets/partners/enoop-food.png';
import lomax from '@/assets/partners/lomax.png';
import tecnovac from '@/assets/partners/tecnovac.png';
import fromfroid from '@/assets/partners/fromfroid.png';

// Import industry content
import { industryContent } from '@/data/industryContent';

const Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const slides = [
    // Slide 1: Title
    {
      title: { en: 'TESCO FOOD TECHNOLOGY', el: 'TESCO FOOD TECHNOLOGY' },
      subtitle: { en: 'Together for Tomorrow', el: 'Together for Tomorrow' },
      tagline: { en: 'Integration • Innovation • Integrity', el: 'Integration • Innovation • Integrity' },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: companyHero,
      showLogo: true
    },
    // Slide 2: Who We Are
    {
      title: { en: 'Who We Are', el: 'Ποιοι Είμαστε' },
      subtitle: { en: 'Technology. Collaboration. Results.', el: 'Τεχνολογία. Συνεργασία. Αποτέλεσμα.' },
      content: {
        en: 'We are a team of engineers designing and implementing process, packaging & automation solutions for the food and beverage industry. From concept to production start-up, we deliver efficiency, safety, and continuous improvement at every stage.',
        el: 'Είμαστε ομάδα μηχανικών που σχεδιάζει και υλοποιεί λύσεις process, packaging & automation για τη βιομηχανία τροφίμων και ποτών. Από τη μελέτη έως την έναρξη παραγωγής, φέρνουμε αποτελεσματικότητα, ασφάλεια και εξέλιξη σε κάθε στάδιο.'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#2563b5]',
      image: heroProcess
    },
    // Slide 3: Our History
    {
      title: { en: 'Our History', el: 'Η Πορεία μας' },
      subtitle: { en: '60+ Years of Excellence', el: '60+ Χρόνια Αριστείας' },
      content: {
        en: 'Since 1963, Tesco Food Technology has been pioneering food processing and packaging solutions, playing a key role in the growth and modernization of the Greek food and beverage industry. With over 60 years of technological excellence and trusted partnerships, we continue to evolve.',
        el: 'Από το 1963, η Tesco Food Technology πρωτοπορεί στις λύσεις επεξεργασίας και συσκευασίας τροφίμων, συμβάλλοντας καθοριστικά στην ανάπτυξη και τον εκσυγχρονισμό της ελληνικής βιομηχανίας τροφίμων και ποτών. Με περισσότερα από 60 χρόνια τεχνολογικής υπεροχής και αμοιβαίας εμπιστοσύνης, συνεχίζουμε να εξελισσόμαστε.'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      image: timelineHistory
    },
    // Slide 4: Vision, Mission & Values (Combined)
    {
      title: { en: 'Vision • Mission • Values', el: 'Όραμα • Αποστολή • Αξίες' },
      subtitle: { en: 'What Drives Us', el: 'Αυτό που μας Οδηγεί' },
      vision: {
        title: { en: 'VISION', el: 'ΟΡΑΜΑ' },
        content: {
          en: 'To pioneer in food technology by integrating world-class process, packaging & lab solutions.',
          el: 'Να ηγούμαστε στις τεχνολογικές εφαρμογές για τη βιομηχανία τροφίμων και ποτών.'
        }
      },
      mission: {
        title: { en: 'MISSION', el: 'ΑΠΟΣΤΟΛΗ' },
        content: {
          en: 'To deliver innovative and reliable turnkey solutions in processing, packaging and automation, building trusted partnerships that create lasting value.',
          el: 'Να παρέχουμε καινοτόμες και αξιόπιστες turnkey λύσεις επεξεργασίας, συσκευασίας και αυτοματισμού, δημιουργώντας σχέσεις εμπιστοσύνης και μακροχρόνια αξία.'
        }
      },
      values: {
        title: { en: 'VALUES', el: 'ΑΞΙΕΣ' },
        items: [
          { en: 'Innovation', el: 'Καινοτομία' },
          { en: 'Integrity', el: 'Ακεραιότητα' },
          { en: 'Courage', el: 'Θάρρος' },
          { en: 'Adaptability', el: 'Προσαρμοστικότητα' },
          { en: 'Teamwork', el: 'Ομαδικότητα' },
          { en: 'Entrepreneurship', el: 'Επιχειρηματικότητα' }
        ]
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]'
    },
    // Slide 6: Industries We Serve
    {
      title: { en: 'Industries We Serve', el: 'Κλάδοι Δραστηριότητας' },
      content: {
        en: 'Dairy • Beverages • Bakery, Grains & Snacks • Meat, Fish & Eggs • Oils & Fats • Spreads & Sauces • Fruit & Vegetables • Ready Meals',
        el: 'Γαλακτοκομικά • Ποτά • Αρτοσκευάσματα, Δημητριακά & Σνακ • Κρέας, Ψάρι & Αυγά • Έλαια & Λίπη • Αλείμματα & Σάλτσες • Φρούτα & Λαχανικά • Έτοιμα Γεύματα'
      },
      details: {
        en: 'Complete solutions for every stage of production with cutting-edge technology, reliability and high food safety standards.',
        el: 'Ολοκληρωμένες λύσεις για κάθε στάδιο παραγωγής με τεχνολογία αιχμής, αξιοπιστία και υψηλά πρότυπα ασφάλειας τροφίμων.'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#1a4280]',
      industryImages: [
        { img: dairyImg, label: { en: 'Dairy', el: 'Γαλακτοκομικά' } },
        { img: beverageImg, label: { en: 'Beverages', el: 'Ποτά' } },
        { img: bakeryImg, label: { en: 'Bakery', el: 'Αρτοσκευάσματα' } },
        { img: meatFishImg, label: { en: 'Meat & Fish', el: 'Κρέας & Ψάρι' } },
        { img: oilsImg, label: { en: 'Oils & Fats', el: 'Έλαια & Λίπη' } },
        { img: spreadsImg, label: { en: 'Spreads', el: 'Αλείμματα' } },
        { img: fruitsVegImg, label: { en: 'Fruits & Vegetables', el: 'Φρούτα & Λαχανικά' } },
        { img: readyMealsImg, label: { en: 'Ready Meals', el: 'Έτοιμα Γεύματα' } }
      ]
    },
    // Industry Slides - Dairy
    {
      title: { en: 'Dairy Solutions', el: 'Λύσεις Γαλακτοκομικών' },
      content: {
        en: industryContent.dairy.overview.en,
        el: industryContent.dairy.overview.el
      },
      details: {
        en: 'Complete solutions: Milk Processing • Yoghurt Production • Cheese Manufacturing • Butter & Ice Cream • Quality Control • End-of-Line Packaging',
        el: 'Ολοκληρωμένες λύσεις: Επεξεργασία Γάλακτος • Παραγωγή Γιαουρτιού • Κατασκευή Τυριού • Βούτυρο & Παγωτό • Ποιοτικός Έλεγχος • Τελική Συσκευασία'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#2563b5]',
      image: dairyImg
    },
    // Industry Slides - Beverages
    {
      title: { en: 'Beverage Solutions', el: 'Λύσεις Ποτών' },
      content: {
        en: industryContent.beverage.overview.en,
        el: industryContent.beverage.overview.el
      },
      details: {
        en: 'Water • Juices • Soft Drinks • Beer • Wine | Complete processing, filling and end-of-line solutions',
        el: 'Νερό • Χυμοί • Αναψυκτικά • Μπύρα • Κρασί | Πλήρεις λύσεις επεξεργασίας, πλήρωσης και τελικής συσκευασίας'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#0F2B5B]',
      image: beverageImg
    },
    // Industry Slides - Bakery
    {
      title: { en: 'Bakery, Grains & Snacks', el: 'Αρτοσκευάσματα, Δημητριακά & Σνακ' },
      content: {
        en: industryContent['bakery-grains-snacks'].overview.en,
        el: industryContent['bakery-grains-snacks'].overview.el
      },
      details: {
        en: 'Complete chain: Dough Preparation • Baking • Packaging • Quality Control',
        el: 'Ολοκληρωμένη αλυσίδα: Προετοιμασία Ζύμης • Ψήσιμο • Συσκευασία • Ποιοτικός Έλεγχος'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#1a4280]',
      image: bakeryImg
    },
    // Industry Slides - Meat & Fish
    {
      title: { en: 'Meat, Fish & Eggs', el: 'Κρέας, Ψάρι & Αυγά' },
      content: {
        en: industryContent['meat-fish-eggs'].overview.en,
        el: industryContent['meat-fish-eggs'].overview.el
      },
      details: {
        en: 'Packaging solutions for meat, fish and liquid eggs • Quality control instruments',
        el: 'Λύσεις συσκευασίας για κρέας, ψάρια και υγρά αυγά • Όργανα ποιοτικού ελέγχου'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: meatFishImg
    },
    // Industry Slides - Oils & Fats
    {
      title: { en: 'Oils & Fats', el: 'Έλαια & Λίπη' },
      content: {
        en: industryContent['oils-fats'].overview.en,
        el: industryContent['oils-fats'].overview.el
      },
      details: {
        en: 'Vegetable oils • Margarines • Specialty fats • Complete processing and packaging',
        el: 'Φυτικά έλαια • Μαργαρίνες • Ειδικά λίπη • Πλήρης επεξεργασία και συσκευασία'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#2563b5]',
      image: oilsImg
    },
    // Industry Slides - Spreads
    {
      title: { en: 'Spreads & Sauces', el: 'Αλείμματα & Σάλτσες' },
      content: {
        en: industryContent['spreads-sauces'].overview.en,
        el: industryContent['spreads-sauces'].overview.el
      },
      details: {
        en: 'Chocolate spreads • Peanut butter • Tahini • Sauces • Honey • Complete solutions',
        el: 'Επάλειμμα σοκολάτας • Φυστικοβούτυρο • Ταχίνι • Σάλτσες • Μέλι • Ολοκληρωμένες λύσεις'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      image: spreadsImg
    },
    // Industry Slides - Fruits & Vegetables
    {
      title: { en: 'Fruits & Vegetables', el: 'Φρούτα & Λαχανικά' },
      content: {
        en: industryContent['fruit-vegetables'].overview.en,
        el: industryContent['fruit-vegetables'].overview.el
      },
      details: {
        en: 'Fresh cut • Frozen • Processing • Packaging • Quality control',
        el: 'Φρέσκα τεμαχισμένα • Κατεψυγμένα • Επεξεργασία • Συσκευασία • Ποιοτικός έλεγχος'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#1a4280]',
      image: fruitsVegImg
    },
    // Industry Slides - Ready Meals
    {
      title: { en: 'Ready Meals', el: 'Έτοιμα Γεύματα' },
      content: {
        en: industryContent['ready-meals'].overview.en,
        el: industryContent['ready-meals'].overview.el
      },
      details: {
        en: 'Complete solutions for ready meal production, packaging and preservation',
        el: 'Ολοκληρωμένες λύσεις για παραγωγή, συσκευασία και διατήρηση έτοιμων γευμάτων'
      },
      background: 'bg-gradient-to-br from-[#1a4280] via-[#2563b5] to-[#0F2B5B]',
      image: readyMealsImg
    },
    // Slide: Our Solutions
    {
      title: { en: 'Our Solutions', el: 'Οι Λύσεις μας' },
      content: {
        en: 'Process Equipment • Liquid Packaging • Solid Packaging • Secondary Packaging • Palletizing & Robotics • Lab & Quality Control • Services & Support',
        el: 'Εξοπλισμός Επεξεργασίας • Συσκευασία Υγρών • Συσκευασία Στερεών • Δευτερογενής Συσκευασία • Παλετοποίηση & Ρομποτική • Εργαστηριακός Έλεγχος • Υπηρεσίες & Υποστήριξη'
      },
      details: {
        en: 'From engineering to commissioning, we deliver turnkey solutions with 24/7 technical support.',
        el: 'Από το σχεδιασμό έως τη θέση σε λειτουργία, παρέχουμε ολοκληρωμένες λύσεις με τεχνική υποστήριξη 24/7.'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#2563b5]',
      image: heroTurnkey
    },
    // Slide: Major Projects & Clients
    {
      title: { en: 'Selected Projects', el: 'Επιλεγμένα Έργα' },
      subtitle: { en: 'Complete project scope:', el: 'Πλήρες αντικείμενο έργου:' },
      content: {
        en: 'Engineering • Equipment Supply • Installation • Commissioning • Training • After-Sales Support',
        el: 'Μηχανολογική Μελέτη • Προμήθεια Εξοπλισμού • Εγκατάσταση • Θέση σε Λειτουργία • Εκπαίδευση • Υποστήριξη'
      },
      clientLogos: true,
      background: 'bg-gradient-to-br from-[#1a4280] via-[#2563b5] to-[#0F2B5B]'
    },
    // Slide: Global Partners
    {
      title: { en: 'Global Partners', el: 'Παγκόσμιοι Συνεργάτες' },
      subtitle: {
        en: 'Strategic partnerships with world-leading manufacturers',
        el: 'Στρατηγικές συνεργασίες με κορυφαίους κατασκευαστές παγκοσμίως'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      partnerLogos: [
        spxflow, seital, plevnik, tecnal, gramEquipment,
        galdi, elopak, trepko, buhmann, gampack, roboteh,
        cartobol, pattyn, serac, foss, xnext,
        comac, cftGroup, mecatherm, vmi, shickEsteve, bfrSystems,
        enoopFood, lomax, tecnovac, fromfroid
      ]
    },
    // Slide: Services & Support
    {
      title: { en: 'Services & Support', el: 'Υπηρεσίες & Υποστήριξη' },
      content: {
        en: '24/7 Technical Support\nGenuine Spare Parts\nPreventive Maintenance\nFast Delivery\nCertified Technicians',
        el: 'Τηλεφωνική Υποστήριξη 24/7\nΓνήσια Ανταλλακτικά\nΠροληπτική Συντήρηση\nΤαχεία Παράδοση\nΠιστοποιημένοι Τεχνικοί'
      },
      details: {
        en: 'Complete technical support for all equipment with immediate response to every support request.',
        el: 'Πλήρης τεχνική υποστήριξη για όλο τον εξοπλισμό με άμεση ανταπόκριση σε κάθε αίτημα.'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: heroServices
    },
    // Slide: Quality & Certifications
    {
      title: { en: 'Quality & Certifications', el: 'Ποιότητα & Πιστοποιήσεις' },
      content: {
        en: 'ISO 9001:2015 Quality Management System\n\nOfficial representatives of leading equipment manufacturers\n\nRecognition for excellence from international organizations',
        el: 'ISO 9001:2015 Σύστημα Διαχείρισης Ποιότητας\n\nΕπίσημοι αντιπρόσωποι κορυφαίων κατασκευαστών εξοπλισμού\n\nΑναγνώριση αριστείας από διεθνείς οργανισμούς'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#0F2B5B]'
    },
    // Slide: Our Commitment
    {
      title: { en: 'Our Commitment', el: 'Η Δέσμευσή μας' },
      content: {
        en: 'We are committed to creating value through innovation, consistency and teamwork — with transparency and trust in every partnership, from design to long-term support.',
        el: 'Δεσμευόμαστε να δημιουργούμε αξία μέσα από καινοτομία, συνέπεια και συνεργασία — με διαφάνεια και εμπιστοσύνη σε κάθε συνεργασία, από τον σχεδιασμό έως τη μακροχρόνια υποστήριξη.'
      },
      subtitle: { en: 'Together for Tomorrow', el: 'Together for Tomorrow' },
      background: 'bg-gradient-to-br from-[#2563b5] via-[#0F2B5B] to-[#1a4280]'
    }
  ];

  const totalSlides = slides.length;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape') {
        navigate('/company');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);


  const currentSlideData = slides[currentSlide];

  return (
    <>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">

        {/* Language Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-16 z-50 text-white hover:bg-white/20"
          onClick={() => {
            const newLang = language === 'en' ? 'el' : 'en';
            localStorage.setItem('language', newLang);
            window.location.reload();
          }}
          title={language === 'en' ? 'Αλλαγή σε Ελληνικά' : 'Switch to English'}
        >
          <div className="flex items-center gap-1">
            <Languages className="h-5 w-5" />
            <span className="text-sm font-bold">{language === 'en' ? 'EL' : 'EN'}</span>
          </div>
        </Button>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
          onClick={() => navigate('/company')}
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Slide Counter */}
        <div className="absolute top-4 left-4 z-50 text-white font-semibold">
          {currentSlide + 1} / {totalSlides}
        </div>


        {/* Slides for Screen - Current slide only */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className={`w-full h-full flex items-center justify-center ${currentSlideData.background} relative overflow-hidden`}
        >
          {/* Background Image with Overlay */}
          {currentSlideData.image && (
            <div className="absolute inset-0">
              <img 
                src={currentSlideData.image} 
                alt={typeof currentSlideData.title === 'string' ? currentSlideData.title : (currentSlideData.title?.[language] || 'TFT Presentation')} 
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F2B5B]/80 via-[#1a4280]/80 to-[#2563b5]/80" />
            </div>
          )}

          <div className="relative z-10 max-w-7xl w-full px-8 md:px-16">
            {/* Industries with images */}
            {currentSlideData.industryImages ? (
              <div className="space-y-8">
                <div className="text-center space-y-4">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  <p className="text-base md:text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
                    {currentSlideData.details[language]}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {currentSlideData.industryImages.map((industry: any, idx: number) => (
                    <div key={idx} className="group relative overflow-hidden rounded-lg border-2 border-white/20 hover:border-white/40 transition-all">
                      <img 
                        src={industry.img}
                        alt={industry.label[language]}
                        className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end justify-center pb-2">
                        <span className="text-white font-semibold text-sm">{industry.label[language]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : currentSlideData.partnerLogos ? (
              /* Partners with logos */
              <div className="space-y-8">
                <div className="text-center space-y-4">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-2xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-3 justify-items-center">
                  {currentSlideData.partnerLogos.map((logo: string, idx: number) => (
                    <div key={idx} className="bg-white/95 rounded-md p-3 flex items-center justify-center hover:bg-white transition-all hover:scale-105 shadow-lg w-full h-20">
                      <img 
                        src={logo}
                        alt={`Partner ${idx + 1}`}
                        className="max-h-12 w-auto object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : currentSlide === 2 ? (
              /* History slide with large image at bottom */
              <div className="space-y-8">
                <div className="text-center space-y-6 md:space-y-8 max-w-5xl mx-auto">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-3xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}

                  {currentSlideData.content && (
                    <p className="text-lg md:text-2xl text-white/80 leading-relaxed whitespace-pre-line">
                      {currentSlideData.content[language]}
                    </p>
                  )}
                </div>

                {currentSlideData.image && (
                  <div className="mt-8">
                    <img 
                      src={currentSlideData.image} 
                      alt={currentSlideData.title[language]}
                      className="rounded-lg shadow-2xl border-4 border-white/20 w-full h-auto max-h-[600px] object-contain mx-auto"
                    />
                  </div>
                )}
              </div>
            ) : currentSlide === 3 ? (
              /* Vision, Mission & Values combined slide */
              <div className="space-y-8 max-w-7xl mx-auto">
                <div className="text-center space-y-4 mb-12">
                  <h1 className="text-5xl md:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  {currentSlideData.subtitle && (
                    <p className="text-2xl md:text-3xl text-white/80">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Vision Card */}
                  <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300">
                    <h2 className="text-3xl font-bold text-white mb-4 tracking-wide">
                      {currentSlideData.vision.title[language]}
                    </h2>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {currentSlideData.vision.content[language]}
                    </p>
                  </div>

                  {/* Mission Card */}
                  <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300">
                    <h2 className="text-3xl font-bold text-white mb-4 tracking-wide">
                      {currentSlideData.mission.title[language]}
                    </h2>
                    <p className="text-lg text-white/90 leading-relaxed">
                      {currentSlideData.mission.content[language]}
                    </p>
                  </div>
                </div>

                {/* Values Section */}
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300">
                  <h2 className="text-3xl font-bold text-white mb-6 text-center tracking-wide">
                    {currentSlideData.values.title[language]}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {currentSlideData.values.items.map((value: any, index: number) => (
                      <div 
                        key={index}
                        className="bg-white/5 border border-white/20 rounded-xl p-4 text-center hover:bg-white/10 hover:scale-105 transition-all duration-300"
                      >
                        <p className="text-lg md:text-xl font-semibold text-white">
                          {value[language]}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : currentSlideData.clientLogos ? (
              /* Selected Projects with client logos */
              <div className="space-y-8 max-w-7xl mx-auto">
                <div className="text-center space-y-6 mb-8">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  {currentSlideData.subtitle && (
                    <p className="text-lg md:text-2xl text-white/70 font-light">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}
                  {currentSlideData.content && (
                    <p className="text-xl md:text-3xl text-white font-semibold leading-relaxed">
                      {currentSlideData.content[language]}
                    </p>
                  )}
                </div>

                {/* Client Logos Grid */}
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                  {[
                    'Δέλτα', 'ΕΒΓΑ', 'Nestlé', 'Δωδώνη',
                    'ΦΑΓΕ', 'ΜΕΒΓΑΛ', 'ΚΡΙ ΚΡΙ', 'ΝΕΟΓΑΛ',
                    'ΗΠΕΙΡΟΣ', 'Friesland Campina', 'ΒΙΒΛΙΑ ΧΩΡΑ', 'ΓΕΡΟΒΑΣΙΛΕΙΟΥ',
                    'Κτήμα ΑΛΦΑ', 'ΣΚΟΥΡΑΣ', 'ΘΕΟΝΗ', 'ΞΥΝΟ ΝΕΡΟ',
                    'CANDIA', 'SEPTEM', 'ΠΑΠΑΔΟΠΟΥΛΟΥ', 'MONDELEZ',
                    'FLORA', 'ΠΕΤΤΑΣ', 'SOYA HELLAS', 'EUROCATERING', 'METLEN'
                  ].map((client, index) => (
                    <div
                      key={index}
                      className="bg-white/95 backdrop-blur-sm rounded-xl p-4 md:p-6 flex items-center justify-center hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg border-2 border-white/30 min-h-[80px]"
                    >
                      <span className="text-base md:text-lg font-bold text-primary text-center">
                        {client}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Regular slides */
              <div className={`${currentSlideData.image ? 'grid md:grid-cols-2 gap-8 items-center' : 'text-center'}`}>
                <div className={`space-y-6 md:space-y-8 ${!currentSlideData.image ? 'max-w-5xl mx-auto' : ''}`}>
                  {currentSlideData.showLogo && (
                    <div className="flex justify-start mb-12">
                      <div className="bg-white/50 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-white/40">
                        <img 
                          src={tftLogo} 
                          alt="Tesco Food Technology"
                          className="h-32 md:h-40 lg:h-48 w-auto object-contain"
                        />
                      </div>
                    </div>
                  )}
                  
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-3xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}

                  {currentSlideData.tagline && (
                    <p className="text-2xl md:text-4xl text-white/95 font-medium tracking-wide">
                      {currentSlideData.tagline[language]}
                    </p>
                  )}

                  {currentSlideData.content && (
                    currentSlide === 14 ? (
                      /* Services with staggered animation and icons */
                      <div className="space-y-3">
                        {(() => {
                          const services = currentSlideData.content[language].split('\n');
                          const icons = [Headset, Package, Wrench, Truck, Award];
                          return services.map((service: string, idx: number) => {
                            const IconComponent = icons[idx];
                            return (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.15, duration: 0.5 }}
                                className="flex items-center gap-4 text-lg md:text-2xl text-white/90 font-medium bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/10 hover:border-white/30 transition-all"
                              >
                                <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-white flex-shrink-0" />
                                <span>{service}</span>
                              </motion.div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="text-lg md:text-2xl text-white/80 leading-relaxed whitespace-pre-line">
                        {currentSlideData.content[language]}
                      </p>
                    )
                  )}

                  {currentSlideData.details && (
                    <p className="text-base md:text-xl text-white/70 leading-relaxed">
                      {currentSlideData.details[language]}
                    </p>
                  )}
                </div>

                {currentSlideData.image && (
                  <div className="hidden md:block">
                    <img 
                      src={currentSlideData.image} 
                      alt={currentSlideData.title[language]}
                      className="rounded-lg shadow-2xl border-4 border-white/20 w-full h-auto max-h-[500px] object-cover"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 no-print"
          onClick={prevSlide}
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12 no-print"
          onClick={nextSlide}
        >
          <ChevronRight className="h-8 w-8" />
        </Button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex gap-2 no-print">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Presentation;
